import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import practiceClient from '../../client/PracticeClient';
import { setPractice } from '../../redux/actions/practiceActions';
import PracticeFormatRow from '../../components/Practice/PracticeFormatRow';
import LearningSettingsPanel from '../../components/Practice/LearningSettingsPanel';
import LearningMethodPanel from '../../components/Practice/LearningMethodPanel';
import DeadlinePanel from '../../components/Practice/DeadlinePanel';
import { boxCountFor } from '../../components/Practice/BoxDistributionBar';
import useDeckPracticeSummary from '../../hooks/useDeckPracticeSummary';
import useSelectedDeck from '../../hooks/useSelectedDeck';
import useTranslation from '../../hooks/useTranslation';
import {
    WORD_PRACTICE_TYPE,
    wordPracticeFormatsFor,
} from '../../constants/wordPractice';
import './WordLearnPage.css';

/**
 * Страница выбора словарной практики: один список форматов вместо пяти карточек.
 *
 * Один запрос сводки на всю колоду при открытии, init — по кнопке в строке. Легенда
 * боксов — один раз наверху, а не по полосе в каждой карточке, и с интервалами
 * повторения из той же сводки: и они, и число боксов — настройка сервера, не клиента.
 */
const WordLearnPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const selectedDeck = useSelectedDeck();
    const deckId = selectedDeck.deckId;

    // Методика колоды приходит с сервера через панель. Здесь она нужна дважды: в
    // запросе на открытие практики и в поиске уже начатой практики того же формата —
    // документ практики опознаётся по тройке, и методика входит в неё.
    //
    // Изначально null, а не значение по умолчанию: панель грузит выбор своим запросом,
    // и если бы кнопка «начать» работала до его конца, клик успел бы открыть практику
    // методикой по умолчанию — или методикой прошлой колоды.
    const [learningMethod, setLearningMethod] = useState(null);
    const [isStartingPlanned, setIsStartingPlanned] = useState(false);

    /**
     * Запуск по плану к дате. Формат берётся первый доступный: план считается по колоде,
     * а не по формату — след памяти у слова один, и «сегодняшняя доля» у всех форматов
     * общая.
     */
    const startPlanned = async () => {
        if (!learningMethod) {
            return;
        }
        setIsStartingPlanned(true);
        try {
            const response = await practiceClient.initializePractice({
                deckId,
                practiceType: WORD_PRACTICE_TYPE,
                practiceFormat: formats[0].format,
                learningMethod,
                sessionMode: 'DEADLINE_REVIEW',
            });
            dispatch(setPractice(
                response.practiceId, WORD_PRACTICE_TYPE, formats[0].format, learningMethod));
            navigate('/word-run');
        } catch (error) {
            console.error('Не удалось начать занятие по плану:', error);
            message.error(t('practice.initFailed'));
            setIsStartingPlanned(false);
        }
    };

    const { summary, isLoading, practiceFor, reload } = useDeckPracticeSummary(
        deckId, WORD_PRACTICE_TYPE, t('practice.summaryFailed'));

    // Глубина методики и интервалы — из сводки: это настройка сервера, не клиента.
    const reviewIntervalDays = summary?.reviewIntervalDays ?? null;
    const boxCount = boxCountFor(reviewIntervalDays);
    // Одна картина на колоду: коробка стала свойством слова, а не пары «формат и слово».
    // Приходит всегда; пустая карта означает колоду без элементов.
    const deckBoxSize = summary?.deckBoxSize ?? null;

    const formats = wordPracticeFormatsFor(selectedDeck.locale?.code);
    const rows = formats.map((entry) => ({ ...entry, practice: practiceFor(entry.format, learningMethod) }));

    // Бейдж «last practised» — у формата, который открывали последним; сравнение по
    // updatedAt, которое init ставит при каждом открытии.
    const latest = rows
        .filter((row) => row.practice?.updatedAt)
        .sort((a, b) => new Date(b.practice.updatedAt) - new Date(a.practice.updatedAt))[0];

    const languagePair = selectedDeck.locale?.name && selectedDeck.translateLocale?.name
        ? `${selectedDeck.locale.name} → ${selectedDeck.translateLocale.name}`
        : null;

    return (
        <div className="wordLearn">
            <div className="wordLearn__head">
                <h4 className="wordLearn__title">
                    {t('practice.choosePractice')}
                    {summary && (
                        <span className="wordLearn__meta">
                            {t('practice.wordsCount', { count: summary.itemCount })}
                            {languagePair && ` · ${languagePair}`}
                        </span>
                    )}
                </h4>
            </div>

            <LearningMethodPanel
                deckId={deckId}
                practiceType={WORD_PRACTICE_TYPE}
                onMethodChange={setLearningMethod}
            />

            <DeadlinePanel
                deckId={deckId}
                practiceType={WORD_PRACTICE_TYPE}
                isStarting={isStartingPlanned}
                onStartPlanned={startPlanned}
            />

            <LearningSettingsPanel />

            <div className="practiceList">
                {rows.map(({ format, titleKey, descriptionKey, practice }) => (
                    <PracticeFormatRow
                        key={format}
                        title={t(titleKey)}
                        description={t(descriptionKey)}
                        practiceRequest={{
                            deckId,
                            practiceType: WORD_PRACTICE_TYPE,
                            practiceFormat: format,
                            learningMethod,
                        }}
                        itemCount={summary?.itemCount ?? 0}
                        practice={practice}
                        boxSize={deckBoxSize}
                        isLoading={isLoading || !learningMethod}
                        isLatest={Boolean(latest) && latest.format === format}
                        boxCount={boxCount}
                        reviewIntervalDays={reviewIntervalDays}
                        onStarted={() => navigate('/word-run')}
                        // Сброс меняет цифры, ради которых на страницу и смотрят:
                        // перечитываем сводку, а не оставляем прежнюю картинку.
                        onReset={reload}
                    />
                ))}
            </div>
        </div>
    );
};

export default WordLearnPage;
